import cn from 'classnames';
import { AccordionContextProvider } from 'components/Accordion/context';
import { EnergyData } from 'components/EnergyData';
import { FavouriteToggle } from 'components/FavouriteToggle';
import { Section } from 'components/Section';
import connect from 'components/Shared/connect';
import { ContentArea } from 'components/Shared/Markdown';
import { isMobile } from 'components/Shared/responsive';
import { VideoPlayer } from 'components/VideoPlayer';
import { ContentArea as ContentAreaModel } from 'models/ContentArea';
import { ProductPageModel } from 'models/ProductPageModel';
import { rm } from 'utils';
import { getQaDataAttr } from '@bygghemmait/utilities';
import { Addons } from 'views/ProductPage/addons';
import { AddonServices } from 'views/ProductPage/addonServices';
import { ImageAttributeProvider } from 'views/ProductPage/configurators/imageAttributeContext';
import { ProductFaqs } from 'views/ProductPage/faqs/themes/BGH';
import { ProductMedia } from 'views/ProductPage/productMedia';
import { REVIEWS_ID } from 'views/ProductPage/reviews';
import { ProductReviews } from 'views/ProductPage/reviews/themes/BGH';
import { Savings } from 'views/ProductPage/savings';
import {
  getProductId,
  getTotalNumberOfReviews,
  getNumberOfFaqs,
  getEfficiencyArrow,
  getEfficiencyDeclarationUrl,
  getProductTitle,
  getProduct
} from 'views/ProductPage/selector';
import { useLoadAdditionalData } from 'views/ProductPage/useLoadAdditionalData';
import { AddToCartSection } from './addToCartSection';
import { Divider } from './divider';
import { ProductAccordion } from './productAccordion';
import { ProductInfoHeader } from './productInfoHeader';
import { ProductPrice } from './productInfoHeader/price';
import { AdminProductAttributes } from './sections/adminProductAttributes';
import { DesktopPackageProduct } from './sections/packageProduct';
import { StandaloneProductAttributes } from './sections/productAttributes';
import { ProductDescription } from './sections/productDescription';
import { ProductSection } from './sections/productSection';
import { ProductShortDescription } from './sections/productShortDescription';
import ProductsInSeries from './sections/productsInSeries';
import { Accessories } from 'views/ProductPage/accessories';
import { Recommended } from 'views/ProductPage/recommended';
import { OthersAlsoBoughtProducts } from 'views/ProductPage/othersAlsoBoughtProducts';
import { ProductPageStructuredData } from 'views/ProductPage/productPageStructuredData';
import { TopSellers } from './sections/topSellers';
import { VariantSelector } from './sections/variantSelector';
import styles from './styles.scss';
import { Enticement } from './enticement';
import { useDispatch } from 'react-redux';
import { productPageLoaded } from '../../actions';
import { useEffect } from 'react';
import { GtmCopyProductTitle } from 'store/googleTagManager/actions';
import { mapProductToGtm } from 'store/googleTagManager/events/utils';
import { GtmProduct } from 'store/googleTagManager/types';
import { DetailedProduct } from 'models/DetailedProduct';
import { PriceGuarantee } from './productInfoHeader/priceGuarantee';
import { RecommendedPrice } from 'views/ProductPage/prices/recommendedPrice';
import { PartPaymentWidget } from './partPaymentWidget';

type MergedProps = Pick<ProductPageModel, 'topContentArea'> & {
  productId: string;
  productTitle: string;
  isMobile: boolean;
  nbrOfReviews: number;
  nbrOfFaqs: number;
  topContentArea?: ContentAreaModel;
  efficiencyArrow?: string;
  efficiencyDeclarationUrl?: string;
  product: DetailedProduct;
};

const _ProductPage = ({
  productId,
  productTitle,
  isMobile,
  nbrOfReviews,
  nbrOfFaqs,
  topContentArea,
  efficiencyArrow,
  efficiencyDeclarationUrl,
  product
}: MergedProps) => {
  const reduxDispatch = useDispatch();

  useLoadAdditionalData();

  useEffect(() => {
    // GTM event for every new product (variants handled separately)
    reduxDispatch(productPageLoaded());
  }, [reduxDispatch, productId]);

  const onCopy = () => {
    reduxDispatch(GtmCopyProductTitle(productTitle, productId));
  };

  const gtmProduct: GtmProduct = mapProductToGtm(product);

  return (
    <>
      <Enticement key={productId} />
      <VideoPlayer />

      <AccordionContextProvider>
        {topContentArea && <ContentArea wrappingComponent={<Section />} {...topContentArea} />}
        <Section>
          <ImageAttributeProvider>
            <div className={cn(styles.productInfoGrid)} {...getQaDataAttr('pdp-infogrid')}>
              {isMobile ? (
                <>
                  <ProductInfoHeader handleCopy={onCopy} />
                  <div className={styles.productMediaWrapperMobile}>
                    <ProductMedia className={styles.productMediaMobile} />
                    <FavouriteToggle
                      className={styles.favourite}
                      productId={productId}
                      gtmProduct={gtmProduct}
                    />
                  </div>
                  <Divider />
                  <Savings />
                  <VariantSelector key={productId} className={styles.variantSelectorMobile} />
                  <AddonServices />
                  <Addons className={styles.addonsMobile} />
                  <RecommendedPrice isProductPage />
                  <ProductPrice isMobile />
                  <EnergyData
                    productId={productId}
                    efficiencyArrow={efficiencyArrow}
                    efficiencyDeclarationUrl={efficiencyDeclarationUrl}
                  />
                  <PriceGuarantee />
                  <PartPaymentWidget />
                </>
              ) : (
                <>
                  <div className={styles.mediaAndFavouriteWrapper}>
                    <ProductMedia />
                    <FavouriteToggle
                      className={styles.favourite}
                      productId={productId}
                      gtmProduct={gtmProduct}
                    />
                  </div>
                  <div className={styles.productInfoHeader}>
                    <ProductInfoHeader handleCopy={onCopy} />
                    <VariantSelector key={productId} className={styles.variantSelector} />
                    <AddonServices />
                    <Addons className={styles.addons} />
                  </div>
                </>
              )}
              <div className={styles.productInfo}>
                <AddToCartSection className={styles.addToCartSection} gtmProduct={gtmProduct} />

                {!isMobile && (
                  <>
                    <DesktopPackageProduct />
                    <ProductSection>
                      <ProductShortDescription />
                    </ProductSection>
                  </>
                )}
              </div>
            </div>
          </ImageAttributeProvider>
          {!isMobile && <AdminProductAttributes />}
        </Section>
        {isMobile ? (
          <ProductAccordion />
        ) : (
          <Section contentClassName={styles.productDescriptionSection}>
            <div>
              <ProductSection title={rm('product_description_header')}>
                <ProductShortDescription hideAttributes hideBrand />
                <ProductDescription />
              </ProductSection>
              {nbrOfReviews > 0 && (
                <ProductSection id={REVIEWS_ID} title={rm('product_section_review_header')}>
                  <ProductReviews />
                </ProductSection>
              )}
              {nbrOfFaqs > 0 && (
                <ProductSection title={rm('product_faq_header')}>
                  <ProductFaqs tabletSize="1" phoneSize="1" />
                </ProductSection>
              )}
            </div>
            <div>
              <StandaloneProductAttributes handleCopy={onCopy} />
            </div>
          </Section>
        )}
        <Section>
          <ProductsInSeries className={styles.relatedProductsSection} />
          <Accessories className={styles.relatedProductsSection} />
          <TopSellers />
          <Recommended />
          <OthersAlsoBoughtProducts className={styles.relatedProductsSection} />
        </Section>
        <ProductPageStructuredData />
      </AccordionContextProvider>
    </>
  );
};

export default connect<MergedProps>(state => ({
  productId: getProductId(state.currentPage),
  productTitle: getProductTitle(state.currentPage),
  isMobile: isMobile(state),
  nbrOfReviews: getTotalNumberOfReviews(state),
  nbrOfFaqs: getNumberOfFaqs(state.currentPage),
  topContentArea: (state.currentPage as ProductPageModel).topContentArea,
  efficiencyArrow: getEfficiencyArrow(state),
  efficiencyDeclarationUrl: getEfficiencyDeclarationUrl(state),
  product: getProduct(state.currentPage)
}))(_ProductPage);
